import React, { useState } from "react";
import Icon from "../Icon";
import { StatusBadge } from "../Badge";

export function RCAPanel({ rca, context = {}, section = "all" }) {
  const [expandedLogs, setExpandedLogs] = useState({});

  const toggleLogs = (idx) => {
    setExpandedLogs((prev) => ({
      ...prev,
      [idx]: !prev[idx],
    }));
  };

  const confidence = rca.confidence_score
    ? Math.round(rca.confidence_score * 100)
    : null;
  const confColor =
    confidence >= 75 ? "#16A34A" : confidence >= 50 ? "#CA8A04" : "#DC2626";
  const confBg =
    confidence >= 75 ? "#F0FDF4" : confidence >= 50 ? "#FEFCE8" : "#FEF2F2";

  // Parse RAG snippet
  const parseRagSnippet = (snippet) => {
    const parts = {
      type: "Historical Context",
      title: "Similar Incident",
      description: "",
      rootCause: "",
      resolution: "",
    };

    if (!snippet) return parts;

    const firstLine = snippet.split("\n")[0] || "";
    const typeMatch = firstLine.match(/^\[(.*?)\]/);
    if (typeMatch) parts.type = typeMatch[1];

    const titleMatch = firstLine.match(/Title:\s*(.*)$/);
    if (titleMatch) parts.title = titleMatch[1];

    const descMatch = snippet.match(
      /Description:\s*([\s\S]*?)(?=Root Cause:|Resolution:|$)/
    );
    if (descMatch) parts.description = descMatch[1].trim();

    const causeMatch = snippet.match(
      /Root Cause:\s*([\s\S]*?)(?=Resolution:|$)/
    );
    if (causeMatch) parts.rootCause = causeMatch[1].trim();

    const resMatch = snippet.match(/Resolution:\s*([\s\S]*?)$/);
    if (resMatch) parts.resolution = resMatch[1].trim();

    return parts;
  };

  const allFindings = rca.evidence?.all_findings || [];
  const ragSnippets = rca.evidence?.rag_snippets || [];
  const applicationName = rca.application_name || context.application_name;
  const serviceName = rca.service_name || rca.evidence?.root_service?.name || context.service_name;
  const hostname = rca.hostname || rca.server_hostname || context.hostname || context.server_hostname;
  const hasDisplayValue = (value) =>
    (typeof value === "string" || typeof value === "number") &&
    String(value).trim().length > 0;
  const rootApplication = rca.evidence?.root_application;
  const rootService = rca.evidence?.root_service;
  const hasApplicationLayerContext =
    hasDisplayValue(applicationName) ||
    hasDisplayValue(rootApplication?.name) ||
    hasDisplayValue(rootApplication?.code) ||
    hasDisplayValue(rootApplication?.tier) ||
    hasDisplayValue(rootService?.name) ||
    hasDisplayValue(rootService?.port) ||
    hasDisplayValue(serviceName) ||
    hasDisplayValue(hostname);
  const showAll = section === "all";
  const showRca = showAll || section === "rca";
  const showSpecialists = showAll || section === "specialists";
  const showNextSteps = showAll || section === "next-steps";
  const showRunbooks = showAll || section === "runbooks";


  const getSpecialistIcon = (name) => {
    const n = String(name).toLowerCase();
    if (n.includes("network")) return "network";
    if (n.includes("db") || n.includes("database")) return "database";
    if (n.includes("system") || n.includes("server") || n.includes("host"))
      return "server";
    return "zap";
  };

  const getDomainTheme = (type) => {
    const t = String(type).toLowerCase();
    if (t.includes("network")) {
      return {
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#DBEAFE",
        icon: "network"
      };
    }
    if (t.includes("db") || t.includes("database")) {
      return {
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#EDE9FE",
        icon: "database"
      };
    }
    if (t.includes("system") || t.includes("server") || t.includes("host")) {
      return {
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FEF3C7",
        icon: "server"
      };
    }
    return {
      color: "#0D9488",
      bg: "#F0FDFA",
      border: "#CCFBF1",
      icon: "activity"
    };
  };

  const renderMetricCell = (key, value) => {
    let displayLabel = key.replace(/_/g, " ");
    let displayValue = "";
    let unit = "";

    // Extract unit suffixes
    if (key.endsWith("_ms")) {
      displayLabel = key.slice(0, -3).replace(/_/g, " ");
      unit = "ms";
    } else if (key.endsWith("_pct")) {
      displayLabel = key.slice(0, -4).replace(/_/g, " ");
      unit = "%";
    } else if (key.endsWith("_sec")) {
      displayLabel = key.slice(0, -4).replace(/_/g, " ");
      unit = "/s";
    } else if (key.endsWith("_connections")) {
      displayLabel = key.slice(0, -12).replace(/_/g, " ");
      unit = " connections";
    }

    displayLabel = displayLabel
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");

    if (typeof value === "number") {
      displayValue = value % 1 === 0 ? value.toLocaleString() : value.toFixed(2);
    } else {
      displayValue = String(value);
    }

    return (
      <div
        key={key}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          background: "#FFFFFF",
          border: "1px solid #E2E8F0",
          borderRadius: 6,
          padding: "8px 10px",
          boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.02)",
        }}
      >
        <span
          style={{
            fontSize: 10,
            color: "#64748B",
            fontWeight: 500,
            textTransform: "capitalize",
          }}
        >
          {displayLabel}
        </span>
        <span
          style={{
            fontSize: 13,
            color: "#0F172A",
            fontWeight: 600,
          }}
        >
          {displayValue}
          {unit && (
            <span style={{ fontSize: 11, fontWeight: 500, color: "#64748B", marginLeft: 2 }}>
              {unit}
            </span>
          )}
        </span>
      </div>
    );
  };

  const highlightLogLine = (line) => {
    if (!line) return "";
    const hasError = line.includes("[ERROR]") || line.includes("level=error") || line.includes("error") || line.includes("i/o timeout");
    const hasWarn = line.includes("[WARN]") || line.includes("level=warn") || line.includes("warn");
    const hasInfo = line.includes("[INFO]") || line.includes("level=info") || line.includes("info");

    let color = "#E2E8F0";
    if (hasError) color = "#FDA4AF"; // light red/rose
    else if (hasWarn) color = "#FDE047"; // light yellow
    else if (hasInfo) color = "#93C5FD"; // light blue

    return (
      <div style={{ color, marginBottom: 2, lineHeight: 1.5 }}>
        {line}
      </div>
    );
  };

  const renderMarkdown = (text) => {
    if (!text) return null;
    const lines = text.split("\n");
    const renderedElements = [];
    let currentList = [];

    const flushList = (key) => {
      if (currentList.length > 0) {
        renderedElements.push(
          <div key={key} style={{ display: "flex", flexDirection: "column", gap: 8, margin: "8px 0" }}>
            {currentList}
          </div>
        );
        currentList = [];
      }
    };

    for (let idx = 0; idx < lines.length; idx++) {
      const line = lines[idx].trim();
      if (!line) continue;

      if (line.startsWith("###")) {
        flushList(`list-${idx}`);
        const headerText = line.replace(/^###\s*/, "");
        renderedElements.push(
          <div
            key={`header-${idx}`}
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#1E293B",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginTop: 14,
              marginBottom: 6,
              display: "flex",
              alignItems: "center",
              gap: 6
            }}
          >
            <span style={{ width: 4, height: 12, background: "#10B981", borderRadius: 2 }} />
            {headerText}
          </div>
        );
      } else if (/^\d+\.\s+/.test(line)) {
        const itemText = line.replace(/^\d+\.\s+/, "");
        const parts = itemText.split(/\*\*(.*?)\*\*/g);
        const content = parts.map((part, pIdx) => {
          if (pIdx % 2 === 1) {
            return <strong key={pIdx} style={{ color: "#0F172A", fontWeight: 600 }}>{part}</strong>;
          }
          return part;
        });

        const stepNum = line.match(/^(\d+)\.\s+/)[1];

        currentList.push(
          <div
            key={`step-${idx}`}
            style={{
              display: "flex",
              gap: 12,
              background: "#F8FAFC",
              border: "1px solid #E2E8F0",
              borderRadius: 6,
              padding: "10px 12px",
              alignItems: "flex-start"
            }}
          >
            <span
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 20,
                height: 20,
                borderRadius: "50%",
                background: "#D1FAE5",
                color: "#065F46",
                fontSize: 10,
                fontWeight: 700,
                flexShrink: 0,
                marginTop: 1
              }}
            >
              {stepNum}
            </span>
            <span style={{ fontSize: 12.5, color: "#334155", lineHeight: 1.5 }}>
              {content}
            </span>
          </div>
        );
      } else {
        flushList(`list-${idx}`);
        const parts = line.split(/\*\*(.*?)\*\*/g);
        const content = parts.map((part, pIdx) => {
          if (pIdx % 2 === 1) {
            return <strong key={pIdx} style={{ color: "#0F172A", fontWeight: 600 }}>{part}</strong>;
          }
          return part;
        });

        renderedElements.push(
          <p key={`p-${idx}`} style={{ fontSize: 12.5, color: "#475569", lineHeight: 1.6, margin: "6px 0" }}>
            {content}
          </p>
        );
      }
    }

    flushList("list-final");
    return renderedElements;
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Badges / Meta */}
      {showRca && <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <StatusBadge status={rca.status || "unknown"} />
        {confidence !== null && (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "3px 9px",
              borderRadius: 20,
              background: confBg,
              color: confColor,
              fontSize: 11,
              fontWeight: 600,
              border: `1px solid ${confidence >= 75 ? "#D1FAE5" : confidence >= 50 ? "#FEF08A" : "#FEE2E2"}`,
            }}
          >
            <div
              style={{
                width: 32,
                height: 6,
                background: "#E2E8F0",
                borderRadius: 3,
                overflow: "hidden",
                display: "inline-block",
              }}
            >
              <div
                style={{
                  width: `${confidence}%`,
                  height: "100%",
                  background: confColor,
                  borderRadius: 3,
                }}
              />
            </div>
            <span>{confidence}% Confidence</span>
          </div>
        )}
        {rca.escalated && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "3px 9px",
              borderRadius: 20,
              background: "#FEF2F2",
              color: "#DC2626",
              fontSize: 11,
              fontWeight: 600,
              border: "1px solid #FEE2E2",
            }}
          >
            <Icon name="alert" size={10} color="#DC2626" /> Escalated
          </span>
        )}
      </div>}

      {/* Root Cause Card */}
      {showRca && rca.root_cause && (
        <div
          style={{
            background: "linear-gradient(135deg, #F8FAFC 0%, #EFF6FF 100%)",
            border: "1px solid #DBEAFE",
            borderLeft: "4px solid #2563EB",
            borderRadius: 8,
            padding: "16px",
            boxShadow: "0 1px 3px 0 rgba(37, 99, 235, 0.04)",
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: "#1E3A8A",
              textTransform: "uppercase",
              letterSpacing: "0.07em",
              marginBottom: 8,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Icon name="search" size={12} color="#2563EB" />
            Root Cause Diagnosis
          </div>
          <p
            style={{
              margin: 0,
              fontSize: 13,
              color: "#0F172A",
              lineHeight: 1.6,
              fontWeight: 500,
            }}
          >
            {rca.root_cause}
          </p>
        </div>
      )}

      {/* Application Context & Blast Radius Traversal Card */}
      {showRca && (rca.evidence?.root_application ||
        rca.evidence?.root_service ||
        applicationName || serviceName || hostname ||
        (rca.evidence?.blast_radius && rca.evidence.blast_radius.length > 0) ||
        (rca.evidence?.affected_applications && rca.evidence.affected_applications.length > 0) ||
        rca.evidence?.blast_radius_summary) && (
        <div
          style={{
            background: "#FFFFFF",
            border: "1px solid #E2E8F0",
            borderLeft: "4px solid #7C3AED",
            borderRadius: 8,
            padding: "16px",
            boxShadow: "0 1px 3px 0 rgba(124, 58, 237, 0.04)",
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: "#6D28D9",
              textTransform: "uppercase",
              letterSpacing: "0.07em",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Icon name="layers" size={13} color="#7C3AED" />
            Application Layer Context &amp; Blast Radius Traversal
          </div>

          {/* Root Application & Service Context Banner */}
          {hasApplicationLayerContext && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: 10,
              background: "#F8FAFC",
              padding: "10px 12px",
              borderRadius: 6,
              border: "1px solid #E2E8F0",
            }}
          >
            {applicationName && (
              <div>
                <div style={{ fontSize: 10, color: "#64748B", fontWeight: 600 }}>APPLICATION</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#0F172A", marginTop: 2 }}>{applicationName}</div>
              </div>
            )}

            {rca.evidence?.root_application && !applicationName && (
              <div>
                <div style={{ fontSize: 10, color: "#64748B", fontWeight: 600 }}>ROOT APPLICATION</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#0F172A", marginTop: 2 }}>
                  {rca.evidence.root_application.name || rca.evidence.root_application.code}
                </div>
                {rca.evidence.root_application.tier && (
                  <span
                    style={{
                      display: "inline-block",
                      marginTop: 4,
                      fontSize: 10,
                      fontWeight: 600,
                      padding: "1px 6px",
                      borderRadius: 4,
                      background: "#EFF6FF",
                      color: "#1D4ED8",
                      border: "1px solid #DBEAFE",
                    }}
                  >
                    {rca.evidence.root_application.tier.toUpperCase()}{" "}
                    {rca.evidence.root_application.type ? `• ${rca.evidence.root_application.type}` : ""}
                  </span>
                )}
              </div>
            )}

            {rca.evidence?.root_service && (
              <div>
                <div style={{ fontSize: 10, color: "#64748B", fontWeight: 600 }}>ROOT PORT SERVICE</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#0F172A", marginTop: 2 }}>
                  {rca.evidence.root_service.name || `Service:${rca.evidence.root_service.port}`}
                </div>
                <span
                  style={{
                    display: "inline-block",
                    marginTop: 4,
                    fontSize: 10,
                    fontWeight: 600,
                    padding: "1px 6px",
                    borderRadius: 4,
                    background: "#FEF3C7",
                    color: "#B45309",
                    border: "1px solid #FDE68A",
                  }}
                >
                  Port {rca.evidence.root_service.port} ({rca.evidence.root_service.protocol || "TCP"})
                </span>
              </div>
            )}
            {serviceName && !rca.evidence?.root_service && (
              <div>
                <div style={{ fontSize: 10, color: "#64748B", fontWeight: 600 }}>SERVICE</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#0F172A", marginTop: 2 }}>{serviceName}</div>
              </div>
            )}
            {hostname && (
              <div>
                <div style={{ fontSize: 10, color: "#64748B", fontWeight: 600 }}>HOSTNAME</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", marginTop: 2, fontFamily: "monospace", wordBreak: "break-word" }}>{hostname}</div>
              </div>
            )}
          </div>
          )}

          {/* AI Blast Radius Narrative Summary */}
          {rca.evidence?.blast_radius_summary && (
            <div
              style={{
                fontSize: 12.5,
                color: "#334155",
                lineHeight: 1.5,
                background: "#FAF5FF",
                padding: "10px 12px",
                borderRadius: 6,
                border: "1px solid #F3E8FF",
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#7C3AED",
                  textTransform: "uppercase",
                  marginBottom: 4,
                }}
              >
                AI Blast Radius Assessment
              </div>
              {rca.evidence.blast_radius_summary}
            </div>
          )}

          {/* Affected Applications List */}
          {rca.evidence?.affected_applications && rca.evidence.affected_applications.length > 0 && (
            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#64748B",
                  textTransform: "uppercase",
                  marginBottom: 6,
                }}
              >
                Potentially Affected Business Applications ({rca.evidence.affected_applications.length})
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {rca.evidence.affected_applications.map((app, aIdx) => (
                  <div
                    key={aIdx}
                    style={{
                      background: "#FEF2F2",
                      border: "1px solid #FECACA",
                      borderRadius: 6,
                      padding: "4px 8px",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 11,
                    }}
                  >
                    <Icon name="layers" size={12} color="#DC2626" />
                    <span style={{ fontWeight: 600, color: "#991B1B" }}>{app.name || app.code}</span>
                    {app.tier && (
                      <span
                        style={{
                          fontSize: 9,
                          color: "#B91C1C",
                          background: "#FEE2E2",
                          padding: "1px 4px",
                          borderRadius: 3,
                        }}
                      >
                        {app.tier}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Multi-hop Blast Radius Traversal Chain */}
          {rca.evidence?.blast_radius && rca.evidence.blast_radius.length > 0 && (
            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#64748B",
                  textTransform: "uppercase",
                  marginBottom: 6,
                }}
              >
                Downstream Dependency Traversal ({rca.evidence.blast_radius.length} Cascade Hops)
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {rca.evidence.blast_radius.map((dep, dIdx) => (
                  <div
                    key={dIdx}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      background: "#F8FAFC",
                      border: "1px solid #E2E8F0",
                      borderRadius: 6,
                      padding: "6px 10px",
                      fontSize: 11.5,
                    }}
                  >
                    <span
                      style={{
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        background: dep.depth === 1 ? "#FEE2E2" : "#FEF3C7",
                        color: dep.depth === 1 ? "#DC2626" : "#D97706",
                        fontWeight: 700,
                        fontSize: 10,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {dep.depth || dIdx + 1}
                    </span>
                    <div style={{ flex: 1 }}>
                      <strong style={{ color: "#0F172A" }}>
                        {dep.service_name || `Service:${dep.port || dep.service_id}`}
                      </strong>
                      {dep.server_name && (
                        <span style={{ color: "#64748B", marginLeft: 6 }}>on {dep.server_name}</span>
                      )}
                    </div>
                    {dep.port && (
                      <span style={{ color: "#64748B", fontFamily: "monospace", fontSize: 11 }}>
                        :{dep.port}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Specialist Analyses */}
      {allFindings.length > 0 && showSpecialists && (
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#475569",
              textTransform: "uppercase",
              letterSpacing: "0.07em",
              marginBottom: 10,
            }}
          >
            Domain Specialist Insights
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {allFindings.map((finding, idx) => {
              const specConfidence = finding.domain_confidence
                ? Math.round(finding.domain_confidence * 100)
                : null;
              const hasMetrics =
                finding.collected_metrics &&
                Object.keys(finding.collected_metrics).length > 0;
              const hasLogs =
                finding.extracted_logs && finding.extracted_logs.length > 0;

              return (
                <div
                  key={idx}
                  style={{
                    background: "#FFFFFF",
                    border: "1px solid #E2E8F0",
                    borderRadius: 8,
                    overflow: "hidden",
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.02)",
                  }}
                >
                  {/* Specialist Header */}
                  <div
                    style={{
                      background: "#F8FAFC",
                      padding: "12px 14px",
                      borderBottom: "1px solid #E2E8F0",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        fontWeight: 600,
                        fontSize: 13,
                        color: "#1E293B",
                        textTransform: "capitalize",
                      }}
                    >
                      <Icon
                        name={getSpecialistIcon(finding.specialist_name)}
                        size={14}
                        color="#2563EB"
                      />
                      {finding.specialist_name} Specialist Findings
                    </div>
                    {specConfidence && (
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: "#1D4ED8",
                          background: "#EFF6FF",
                          border: "1px solid #DBEAFE",
                          padding: "2px 8px",
                          borderRadius: 12,
                        }}
                      >
                        {specConfidence}% Match
                      </span>
                    )}
                  </div>

                  {/* Specialist Body */}
                  <div
                    style={{
                      padding: 14,
                      display: "flex",
                      flexDirection: "column",
                      gap: 12,
                    }}
                  >
                    {/* Findings list */}
                    {finding.findings && finding.findings.length > 0 && (
                      <ul
                        style={{
                          margin: 0,
                          paddingLeft: 20,
                          fontSize: 12.5,
                          color: "#334155",
                          lineHeight: 1.6,
                        }}
                      >
                        {finding.findings.map((f, fIdx) => (
                          <li key={fIdx} style={{ marginBottom: 4 }}>
                            {f}
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Metrics Section */}
                    {hasMetrics && (
                      <div>
                        <div
                          style={{
                            fontSize: 10,
                            color: "#94A3B8",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            marginBottom: 6,
                          }}
                        >
                          Collected Metrics
                        </div>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))",
                            gap: 8,
                          }}
                        >
                          {Object.entries(finding.collected_metrics).map(([k, v]) =>
                            renderMetricCell(k, v)
                          )}
                        </div>
                      </div>
                    )}

                    {/* Logs Section */}
                    {hasLogs && (
                      <div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 6,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 10,
                              color: "#94A3B8",
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: "0.05em",
                            }}
                          >
                            Extracted Logs ({finding.extracted_logs.length})
                          </span>
                          <button
                            onClick={() => toggleLogs(idx)}
                            style={{
                              fontSize: 11,
                              color: "#2563EB",
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              padding: 0,
                            }}
                          >
                            {expandedLogs[idx] ? "Collapse" : "Expand"}
                          </button>
                        </div>
                        <div
                          style={{
                            background: "#0F172A",
                            borderRadius: 6,
                            padding: "10px 12px",
                            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                            fontSize: 11,
                            color: "#E2E8F0",
                            maxHeight: expandedLogs[idx] ? "none" : 88,
                            overflowY: expandedLogs[idx] ? "visible" : "hidden",
                            border: "1px solid #1E293B",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-all",
                          }}
                        >
                          {finding.extracted_logs.map((line, lIdx) =>
                            highlightLogLine(line)
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Suggested Steps */}
      {showNextSteps && rca.suggestions && rca.suggestions.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#475569",
              textTransform: "uppercase",
              letterSpacing: "0.07em",
            }}
          >
            Recommended Next Steps
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {rca.suggestions.map((suggestion, idx) => {
              const theme = getDomainTheme(suggestion.suggestion_type);
              return (
                <div
                  key={idx}
                  style={{
                    background: "#FFFFFF",
                    border: `1px solid ${theme.border}`,
                    borderRadius: 8,
                    overflow: "hidden",
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.02)",
                  }}
                >
                  {/* Step Header */}
                  <div
                    style={{
                      background: theme.bg,
                      padding: "10px 12px",
                      borderBottom: `1px solid ${theme.border}`,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span
                        style={{
                          background: theme.color,
                          color: "#FFFFFF",
                          fontSize: 10,
                          fontWeight: 700,
                          width: 16,
                          height: 16,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {idx + 1}
                      </span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: theme.color, textTransform: "uppercase" }}>
                        {suggestion.suggestion_type ? suggestion.suggestion_type.replace(/_/g, " ") : "remediation"} Actions
                      </span>
                    </div>
                  </div>

                  {/* Step Body */}
                  <div style={{ padding: "12px 14px" }}>
                    {renderMarkdown(suggestion.suggestion_text)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Knowledge Base Snippets */}
      {showRunbooks && ragSnippets.length > 0 && (
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#475569",
              textTransform: "uppercase",
              letterSpacing: "0.07em",
              marginBottom: 10,
            }}
          >
            Correlated KB & Runbooks
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {ragSnippets.map((snippet, idx) => {
              const parsed = parseRagSnippet(snippet);
              return (
                <div
                  key={idx}
                  style={{
                    background: "#FFFFFF",
                    border: "1px solid #E2E8F0",
                    borderRadius: 8,
                    padding: 12,
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.02)",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 8 }}>
                    <div>
                      <div
                        style={{
                          fontSize: 9,
                          fontWeight: 700,
                          color: "#64748B",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          marginBottom: 2,
                        }}
                      >
                        {parsed.type}
                      </div>
                      <h4 style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#1E293B" }}>
                        {parsed.title}
                      </h4>
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 12, color: "#475569" }}>
                    {parsed.description && (
                      <p style={{ margin: 0, lineHeight: 1.5 }}>
                        <strong>Symptoms:</strong> {parsed.description}
                      </p>
                    )}
                    {parsed.rootCause && (
                      <p style={{ margin: 0, lineHeight: 1.5 }}>
                        <strong>Historical Cause:</strong> {parsed.rootCause}
                      </p>
                    )}
                    {parsed.resolution && (
                      <p
                        style={{
                          margin: 0,
                          lineHeight: 1.5,
                          background: "#ECFDF5",
                          border: "1px solid #A7F3D0",
                          borderRadius: 6,
                          padding: "6px 8px",
                          color: "#065F46",
                        }}
                      >
                        <strong>Resolution Steps:</strong> {parsed.resolution}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Fallback empty state */}
      {!showAll && (
        (section === "rca" && !rca.root_cause && !applicationName && !serviceName && !hostname && !rca.evidence?.root_application && !rca.evidence?.root_service && !rca.evidence?.blast_radius?.length && !rca.evidence?.affected_applications?.length && !rca.evidence?.blast_radius_summary) ||
        (section === "specialists" && !allFindings.some((finding) => (finding.findings?.length || 0) > 0 || (finding.collected_metrics && Object.keys(finding.collected_metrics).length > 0) || finding.extracted_logs?.length)) ||
        (section === "next-steps" && !rca.suggestions?.length) ||
        (section === "runbooks" && !ragSnippets.length)
      ) && (
        <div
          style={{
            textAlign: "center",
            padding: 20,
            color: "#9CA3AF",
            fontSize: 13,
          }}
        >
          {section === "rca" ? "Root cause analysis is pending or has no specialist findings yet." : "No information is available in this section yet."}
        </div>
      )}
      {showAll && !rca.root_cause && !rca.suggestions?.length && allFindings.length === 0 && (
        <div style={{ textAlign: "center", padding: 20, color: "#9CA3AF", fontSize: 13 }}>
          Analysis pending or incomplete.
        </div>
      )}
    </div>
  );
}

export default RCAPanel;
